import { useEffect } from 'react';
import { cn } from 'lib/utils';
import { Icon, type IconName } from 'components/ui/icon';

export enum ToastType {
  success = 'success',
  warning = 'warning',
  error = 'error',
}

interface IProps {
  showToast: boolean;
  onClose: (arg: boolean) => void;
  message: string;
  details?: JSX.Element;
  type?: ToastType;
  showCross?: boolean;
  id?: string;
  className?: string;
}

const ICON_BY_TYPE: Record<ToastType, IconName> = {
  [ToastType.success]: 'check-circle',
  [ToastType.error]: 'x-circle',
  [ToastType.warning]: 'alert-circle',
};

const ICON_COLOR_BY_TYPE: Record<ToastType, string> = {
  [ToastType.success]: 'text-green-400',
  [ToastType.error]: 'text-red-400',
  [ToastType.warning]: 'text-yellow-400',
};

const BORDER_BY_TYPE: Record<ToastType, string> = {
  [ToastType.success]: 'border-l-green-400',
  [ToastType.error]: 'border-l-red-400',
  [ToastType.warning]: 'border-l-yellow-400',
};

const ToastContainer = ({
  showToast,
  onClose,
  type = ToastType.success,
  message,
  details,
  showCross = true,
  id = '',
  className = '',
}: IProps): JSX.Element | null => {
  useEffect(() => {
    if (showToast) {
      const timeout = setTimeout(() => {
        onClose(false);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [showToast, onClose]);

  if (!showToast) return null;

  return (
    <div
      data-testid={`${id ? `${id}-` : ''}toast-${type}`}
      role="alert"
      className={cn(
        'fixed top-20 right-4 z-50 min-w-80 max-w-105',
        'bg-white rounded-lg shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)]',
        'border-l-4',
        BORDER_BY_TYPE[type],
        className
      )}
    >
      <div className="pt-3 pb-2 px-3 flex items-center">
        <Icon
          name={ICON_BY_TYPE[type]}
          size={20}
          className={cn('ml-2 shrink-0', ICON_COLOR_BY_TYPE[type])}
          aria-hidden="true"
        />
        <span
          className={cn(
            'ml-2 text-sm leading-5 text-gray-blue-90',
            details ? 'font-semibold' : 'font-medium'
          )}
        >
          {message}
        </span>
        {showCross ? (
          <button
            type="button"
            onClick={() => onClose(false)}
            aria-label="Close toast"
            className="ml-auto mr-1 align-middle cursor-pointer text-gray-blue-60 hover:text-gray-blue-90 transition-colors bg-transparent border-0 p-0"
          >
            <Icon name="x" size={20} aria-hidden="true" />
          </button>
        ) : (
          <span className="ml-auto" />
        )}
      </div>
      {details && (
        <div className="px-3 pb-3 ml-9 flex items-center w-full">
          {details}
        </div>
      )}
    </div>
  );
};

export default ToastContainer;
